import { supabase, SUPABASE_URL } from '@/integrations/supabase/client';

export type BucketName = 'admin.assets' | 'client.photo' | 'practitioner.photo' | 'studio.logo' | 'space.photos';

/**
 * Uploads a gallery photo with a unique filename
 * @param bucket The name of the storage bucket
 * @param userId The user ID to create folder structure
 * @param file The file to upload
 * @returns Object containing the file path and public URL
 */
export const uploadGalleryPhoto = async (bucket: BucketName, userId: string, file: File) => {
  try {
    // Validate file
    if (!file) {
      throw new Error('No file provided to upload');
    }
    
    if (!file.type.startsWith('image/')) {
      console.error(`Invalid file type detected: ${file.type}`);
      throw new Error(`Invalid file type: ${file.type}. Only image files are allowed.`);
    }
    
    if (file.size === 0) {
      throw new Error('File is empty (0 bytes). Cannot upload empty files.');
    }
    
    // Get file extension based on MIME type
    let fileExt = 'jpg';
    if (file.type === 'image/png') {
      fileExt = 'png';
    } else if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      fileExt = 'jpg';
    } else if (file.type === 'image/gif') {
      fileExt = 'gif';
    } else if (file.type === 'image/webp') {
      fileExt = 'webp';
    }
    
    // Create unique file path for gallery photos
    const timestamp = Date.now();
    const filePath = `${userId}/gallery_${timestamp}.${fileExt}`;
    
    console.log(`Uploading gallery photo to ${bucket}/${filePath}`);
    console.log(`File details: name=${file.name}, type=${file.type}, size=${file.size} bytes`);
    
    const storageClient = supabase.storage.from(bucket);
    
    // Upload the file without deleting existing files
    const { data, error } = await storageClient.upload(filePath, file, { 
      contentType: file.type,
      cacheControl: 'public,max-age=3600',
      upsert: false
    });

    if (error) {
      console.error('Error in gallery photo upload:', error);
      throw error;
    }

    console.log(`Gallery photo upload successful for ${filePath}`);
    
    // Get the public URL for the uploaded file
    const { data: urlData } = storageClient.getPublicUrl(filePath);

    return {
      path: filePath,
      publicUrl: urlData.publicUrl
    };
  } catch (error) {
    console.error('Error in uploadGalleryPhoto:', error);
    throw error;
  }
};

/**
 * Uploads a file to the specified Supabase storage bucket using a fixed filename strategy
 * @param bucket The name of the storage bucket
 * @param userId The user ID to create folder structure
 * @param file The file to upload
 * @returns Object containing the file path and public URL
 */
export const uploadAvatar = async (bucket: BucketName, userId: string, file: File) => {
  try {
    // Validate file
    if (!file) {
      throw new Error('No file provided to upload');
    }
    
    if (!file.type.startsWith('image/')) {
      console.error(`Invalid file type detected: ${file.type}`);
      throw new Error(`Invalid file type: ${file.type}. Only image files are allowed.`);
    }
    
    if (file.size === 0) {
      throw new Error('File is empty (0 bytes). Cannot upload empty files.');
    }
    
    // Get file extension based on MIME type
    let fileExt = 'jpg';
    if (file.type === 'image/png') {
      fileExt = 'png';
    } else if (file.type === 'image/jpeg' || file.type === 'image/jpg') {
      fileExt = 'jpg';
    } else if (file.type === 'image/gif') {
      fileExt = 'gif';
    } else if (file.type === 'image/webp') {
      fileExt = 'webp';
    }
    
    // Create consistent file path with fixed filename
    const filePath = `${userId}/avatar.${fileExt}`;
    
    console.log(`Uploading avatar to ${bucket}/${filePath}`);
    console.log(`File details: name=${file.name}, type=${file.type}, size=${file.size} bytes`);
    
    const storageClient = supabase.storage.from(bucket);
    
    // Step 1: List and delete any existing files in the user's folder
    const { data: existingFiles } = await storageClient.list(userId);
    if (existingFiles && existingFiles.length > 0) {
      const filesToDelete = existingFiles.map(item => `${userId}/${item.name}`);
      console.log('Removing existing files:', filesToDelete);
      await storageClient.remove(filesToDelete);
    }
    
    // Step 2: Upload the new file with proper content type
    // IMPORTANT: Debug logging for upload operation
    console.log('Starting file upload with these options:', { 
      contentType: file.type,
      cacheControl: 'public,max-age=3600',
      upsert: false 
    });
    
    // Try direct upload first
    const { data, error } = await storageClient.upload(filePath, file, { 
      contentType: file.type,
      cacheControl: 'public,max-age=3600',
      upsert: false // Avoid metadata retention issues
    });

    if (error) {
      console.error('Error in standard upload method:', error);
      
      // If standard upload fails, try the fallback method with direct fetch API
      return await uploadAvatarFallback(bucket, userId, filePath, file);
    }

    console.log(`Upload successful for ${filePath}`);
    
    // Step 3: Get the public URL for the uploaded file
    const { data: urlData } = storageClient.getPublicUrl(filePath);

    return {
      path: filePath,
      publicUrl: urlData.publicUrl
    };
  } catch (error) {
    console.error('Error in uploadAvatar:', error);
    throw error;
  }
};

/**
 * Fallback method for avatar upload using direct fetch API
 * This bypasses potential issues with the Supabase client's content-type handling
 */
const uploadAvatarFallback = async (
  bucket: BucketName, 
  userId: string, 
  filePath: string, 
  file: File
): Promise<{ path: string; publicUrl: string }> => {
  try {
    console.log('Using fallback upload method with direct fetch');
    
    // Create form data
    const formData = new FormData();
    formData.append('file', file);
    
    // Get the file upload URL from Supabase
    const uploadUrl = `${SUPABASE_URL}/storage/v1/object/${bucket}/${filePath}`;
    
    // Get the token
    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token || '';
    
    // Perform the upload with fetch API
    const response = await fetch(uploadUrl, {
      method: 'POST',
      body: formData,
      headers: {
        'Authorization': `Bearer ${token}`,
        // Note: Don't set Content-Type as the browser will set it correctly for FormData
      },
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Fallback upload failed: ${response.status} - ${errorText}`);
    }
    
    console.log('Fallback upload successful');
    
    // Get the public URL for the uploaded file
    const storageClient = supabase.storage.from(bucket);
    const { data: urlData } = storageClient.getPublicUrl(filePath);
    
    return {
      path: filePath,
      publicUrl: urlData.publicUrl
    };
  } catch (error) {
    console.error('Error in fallback upload method:', error);
    throw error;
  }
};

/**
 * Gets the public URL for a file in a storage bucket
 * @param bucket The name of the storage bucket
 * @param filePath The file path within the bucket
 * @returns The public URL for the file
 */
export const getAvatarUrl = (bucket: BucketName, filePath: string): string => {
  if (!filePath) {
    console.warn('getAvatarUrl called with empty filePath');
    return '';
  }
  
  try {
    const { data } = supabase.storage
      .from(bucket)
      .getPublicUrl(filePath);
    
    // Log the generated URL for debugging
    console.log(`Generated avatar URL: ${data.publicUrl}`);
    
    return data.publicUrl;
  } catch (error) {
    console.error('Error generating avatar URL:', error);
    return '';
  }
};

/**
 * Deletes a file from a storage bucket
 * @param bucket The name of the storage bucket
 * @param filePath The file path to delete
 * @returns Success status
 */
export const deleteFile = async (bucket: BucketName, filePath: string): Promise<boolean> => {
  try {
    if (!filePath) {
      console.log('No file path provided for deletion - skipping');
      return true; // Nothing to delete
    }
    
    console.log(`Attempting to delete file: ${bucket}/${filePath}`);
    
    const { error } = await supabase.storage
      .from(bucket)
      .remove([filePath]);
    
    if (error) {
      console.error('Error deleting file:', error);
      return false;
    }
    
    console.log(`Successfully deleted file: ${bucket}/${filePath}`);
    return true;
  } catch (error) {
    console.error('Unexpected error deleting file:', error);
    return false;
  }
};

/**
 * Updates a user's avatar, handling deletion of the old avatar if it exists
 * @param bucket The storage bucket name
 * @param userId The user's ID
 * @param file The new avatar file
 * @returns Object with the new path and public URL
 */
export const updateAvatar = async (
  bucket: BucketName,
  userId: string,
  file: File
): Promise<{ path: string; publicUrl: string }> => {
  try {
    // Upload the new avatar (uploadAvatar function already handles deleting existing files)
    console.log('Uploading new avatar');
    const result = await uploadAvatar(bucket, userId, file);
    console.log('Avatar upload result:', result);
    
    return result;
  } catch (error) {
    console.error('Error in updateAvatar:', error);
    throw error;
  }
};

/**
 * Utility to clean up problematic avatar files for a user
 * @param bucket The storage bucket name
 * @param userId The user's ID 
 */
export const cleanupUserAvatars = async (
  bucket: BucketName, 
  userId: string
): Promise<void> => {
  try {
    console.log(`Cleaning up avatar files for user ${userId} in ${bucket}`);
    const storageClient = supabase.storage.from(bucket);
    
    // List all files in the user's folder
    const { data: userFiles, error: listError } = await storageClient.list(userId);
    
    if (listError) {
      console.error('Error listing user files:', listError);
      return;
    }
    
    if (!userFiles || userFiles.length === 0) {
      console.log(`No files found for user ${userId}`);
      return;
    }
    
    console.log(`Found ${userFiles.length} files for user ${userId}`);
    
    // Delete all files in the user folder - we'll only keep one avatar with the proper naming
    const filesToDelete = userFiles.map(file => `${userId}/${file.name}`);
    
    if (filesToDelete.length > 0) {
      console.log(`Deleting ${filesToDelete.length} files:`, filesToDelete);
      await storageClient.remove(filesToDelete)
        .catch(err => console.error('Error removing files:', err));
    }
  } catch (error) {
    console.error('Error cleaning up user avatars:', error);
  }
};
